/**
 * Reshaping between what a form's exporter hands over and what its boxes
 * print.
 *
 * The exporter's shapes answer to the PDF's field names — a signature object
 * here, a keyed check-off map there — so the flattening lives apart from the
 * cards, where it can be read and tested on its own.
 */
import { ICS221_CHECKOFF_UNITS } from './documentBoxLabels';

type Signature = {
  name?: string;
  positionTitle?: string;
  role?: string;
  signedAt?: number;
};

/** The 207 hands its Prepared By over as one signature object. */
export function flattenPreparedBy(
  inputs: Record<string, unknown>
): Record<string, unknown> {
  const signature = (inputs.preparedBy ?? {}) as Signature;
  return {
    ...inputs,
    preparedByName: signature.name ?? '',
    preparedByPosition: signature.positionTitle ?? signature.role ?? '',
    preparedDateTime: signature.signedAt
      ? new Date(signature.signedAt).toLocaleString()
      : '',
  };
}

type Checkoff = {
  checked?: boolean;
  name?: string;
  remarks?: string;
  datetime?: string | Date;
};

export type Ics221CheckoffRow = {
  unit: string;
  checked: boolean;
  remarks: string;
  name: string;
  signedAt: string;
};

/**
 * Box 6 prints one row per unit that has to sign a resource out, in the order
 * the sheet lists them, with any unit the incident added underneath — the
 * blank lines the printed form leaves for "any additional unit requirements".
 */
export function ics221CheckoffRows(
  inputs: Record<string, unknown>
): Record<string, unknown> {
  const checkoffs = (inputs.checkoutCheckoffs ?? {}) as Record<string, Checkoff>;
  const custom = (inputs.customUnits ?? []) as {
    unitName: string;
    checked?: boolean;
    name?: string;
    remarks?: string;
  }[];

  const row = (unit: string, entry: Checkoff | undefined): Ics221CheckoffRow => ({
    unit,
    checked: entry?.checked ?? false,
    remarks: entry?.remarks ?? '',
    name: entry?.name ?? '',
    signedAt: entry?.datetime ? new Date(entry.datetime).toLocaleString() : '',
  });

  return {
    ...inputs,
    checkoffRows: [
      ...ICS221_CHECKOFF_UNITS.map((unit) =>
        row(unit.label, checkoffs[unit.key])
      ),
      ...custom.map((unit) =>
        row(unit.unitName, {
          checked: unit.checked,
          name: unit.name,
          remarks: unit.remarks,
        })
      ),
    ],
  };
}
